import React, { useState } from 'react'
import './CfoSite.css'
import { CfoBrandMark, CfoSiteFooter, navigateTo } from './CfoSiteShared'

type Plan = {
  id: string
  name: string
  forWho: string
  monthly: number
  annual: number
  cta: string
  onCta: () => void
  featured?: boolean
  priceNote?: string
  lead: string
  bullets: string[]
}

const SALES_EMAIL = 'hello@fusegrid.io'

const goSignup = () => navigateTo('/signup')
const goSales = () => {
  if (typeof window !== 'undefined') window.location.href = `mailto:${SALES_EMAIL}?subject=CFO%20Core%20—%20multi-company`
}
const goPartner = () => {
  navigateTo('/signup?plan=partner')
}

const PLANS: Plan[] = [
  {
    id: 'essentials',
    name: 'Essentials',
    forWho: 'For owner-operators who finally want to understand their numbers.',
    monthly: 99,
    annual: 79,
    cta: 'Start free trial',
    onCta: goSignup,
    lead: 'Know where you stand',
    bullets: [
      'Your profit, cash, and balance — straight from a supported accounting platform, always up to date',
      'See who owes you money and who’s overdue',
      'A plain-English health check every month, vs. businesses like yours',
      'Click any number to see exactly what’s behind it',
      'Ask questions about your business in plain English',
      'Unlimited team members — invite anyone, free',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    forWho: 'For founders and finance leads who need to see what’s coming.',
    monthly: 349,
    annual: 279,
    cta: 'Start free trial',
    onCta: goSignup,
    featured: true,
    lead: 'Everything in Essentials, plus look ahead',
    bullets: [
      'See your cash and profit months ahead — a forecast you can trust',
      'Try big decisions before you make them — a hire, a price change, a slow quarter',
      'Know what your business is worth',
      'Smarter collections — see who’s likely to pay late',
      'Share-ready board & investor reports with one link',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    forWho: 'For groups running several companies under one roof.',
    monthly: 899,
    annual: 749,
    cta: 'Talk to us',
    onCta: goSales,
    priceNote: '5 companies · +$129 each after',
    lead: 'Everything in Pro, plus run it all together',
    bullets: [
      'Combine multiple companies into one view',
      'Control who can see what',
      'Your branding on every report and shared link',
      'Priority support and onboarding',
    ],
  },
]

type CmpRow = { label: string; e: React.ReactNode; p: React.ReactNode; x: React.ReactNode }
const yes = <span className="yes" aria-label="included">✓</span>
const no = <span className="no" aria-label="not included">—</span>
const CMP: { group: string; rows: CmpRow[] }[] = [
  {
    group: 'Know where you stand',
    rows: [
      { label: 'Profit, cash & balance — always current', e: yes, p: yes, x: yes },
      { label: 'See who owes you money', e: yes, p: yes, x: yes },
      { label: 'Monthly health check + industry comparison', e: yes, p: yes, x: yes },
      { label: 'Ask questions in plain English', e: yes, p: yes, x: yes },
    ],
  },
  {
    group: 'See what’s coming & decide',
    rows: [
      { label: 'Cash & profit forecast you can trust', e: no, p: yes, x: yes },
      { label: 'What-if scenarios for big decisions', e: no, p: yes, x: yes },
      { label: 'Know what your business is worth', e: no, p: yes, x: yes },
      { label: 'Predict who’ll pay late', e: no, p: yes, x: yes },
      { label: 'Share-ready board & investor reports', e: no, p: yes, x: yes },
    ],
  },
  {
    group: 'Run several companies',
    rows: [
      { label: 'Combine multiple companies into one view', e: no, p: no, x: yes },
      { label: 'Control who sees what', e: no, p: no, x: yes },
      { label: 'White-label your reports', e: no, p: no, x: yes },
    ],
  },
  {
    group: 'Every plan',
    rows: [
      { label: 'Companies included', e: <span className="val">1</span>, p: <span className="val">1</span>, x: <span className="val">5</span> },
      { label: 'Team members', e: <span className="val">Unlimited</span>, p: <span className="val">Unlimited</span>, x: <span className="val">Unlimited</span> },
    ],
  },
]

const FAQS: { q: string; a: string }[] = [
  {
    q: 'Is my accounting data safe?',
    a: 'Yes. Available connections are read-only — CFO Core can read your books but can never change them. Your data and provider credentials are encrypted, and you can disconnect at any time.',
  },
  {
    q: 'Do I need to be a finance person?',
    a: 'No. CFO Core is built for business owners, not accountants. It explains everything in plain English and answers your questions directly — no spreadsheets, no jargon.',
  },
  {
    q: 'How does pricing work?',
    a: 'One flat price per company — not per person. Invite your whole team and your accountant for free. Pay monthly, or save about 20% paying annually. Cancel anytime.',
  },
  {
    q: 'Can I try it before I pay?',
    a: 'Yes. Start a free trial with full Pro features so you can see your own numbers and forecast before you decide.',
  },
  {
    q: 'What if I have more than one company?',
    a: 'Connect each one. Enterprise combines them into a single view at a lower price per company, so groups and holdcos see the whole picture.',
  },
  {
    q: 'Do you work with my accountant?',
    a: 'Absolutely — invite them for free. And if you’re an accounting or bookkeeping firm, ask about our partner plan built for serving many clients.',
  },
]

export function CfoPricingPage() {
  const [annual, setAnnual] = useState(false)
  const goHome = () => navigateTo('/')
  const goLogin = () => navigateTo('/login')

  return (
    <div className="cfo-site">
      <nav className="cfo-site-nav">
        <span onClick={goHome} style={{ cursor: 'pointer' }}>
          <CfoBrandMark sub="by FuseGrid" />
        </span>
        <div className="cfo-site-nav-links">
          <button className="cfo-btn cfo-btn-ghost" onClick={goLogin}>Sign in</button>
          <button className="cfo-btn cfo-btn-primary" onClick={goSignup}>Connect your books</button>
        </div>
      </nav>

      <header className="cfo-hero" style={{ textAlign: 'center', paddingBottom: 24 }}>
        <div className="cfo-wrap">
          <span className="cfo-eyebrow">Pricing</span>
          <h1 style={{ maxWidth: 760, margin: '14px auto 0' }}>
            One simple price <em>per company</em>. Cancel anytime.
          </h1>
          <p className="lede" style={{ margin: '18px auto 0' }}>
            Everything you need to understand your business and plan ahead — with no per-seat fees.
            Invite your whole team and your accountant for free.
          </p>
          <div className="cfo-price-toggle" role="group" aria-label="Billing period">
            <button className={annual ? '' : 'active'} onClick={() => setAnnual(false)}>Monthly</button>
            <button className={annual ? 'active' : ''} onClick={() => setAnnual(true)}>
              Annual <span className="save">save ~20%</span>
            </button>
          </div>
        </div>
      </header>

      <section className="cfo-section" style={{ paddingTop: 8 }}>
        <div className="cfo-wrap">
          <div className="cfo-plans">
            {PLANS.map((plan) => (
              <div className={`cfo-card cfo-plan${plan.featured ? ' featured' : ''}`} key={plan.id}>
                {plan.featured && <span className="cfo-plan-badge">Most popular</span>}
                <h3 className="cfo-plan-name">{plan.name}</h3>
                <p className="cfo-plan-for">{plan.forWho}</p>
                <div className="cfo-price-row num">
                  <span className="cfo-price">${annual ? plan.annual : plan.monthly}</span>
                  <span className="cfo-price-suffix">/mo</span>
                </div>
                <p className="cfo-price-note">
                  {plan.priceNote ? plan.priceNote : annual ? 'billed annually' : 'billed monthly'}
                </p>
                <button
                  className={`cfo-btn ${plan.featured ? 'cfo-btn-primary' : 'cfo-btn-ghost'}`}
                  onClick={plan.onCta}
                >
                  {plan.cta}
                </button>
                <p className="cfo-plan-lead">{plan.lead}</p>
                <ul className="cfo-plan-list">
                  {plan.bullets.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="cfo-firm">
            <div className="cfo-firm-copy">
              <h3>For accountants & bookkeepers</h3>
              <p>
                Give every client a CFO. White-label the reports, manage all your clients in one place,
                and use CFO Core free on your own books. Volume pricing kicks in at 10, 25, and 50 clients.
              </p>
            </div>
            <div className="cfo-firm-price">
              <span className="amt num">from $69<span> /client/mo</span></span>
              <button className="cfo-btn cfo-btn-primary" onClick={goPartner}>Start Partner trial</button>
            </div>
          </div>
        </div>
      </section>

      <section className="cfo-section alt">
        <div className="cfo-wrap">
          <span className="cfo-eyebrow">Compare plans</span>
          <h2>What each plan does for you.</h2>
          <div className="cfo-cmp-wrap">
            <table className="cfo-cmp num">
              <thead>
                <tr>
                  <th>Outcome</th>
                  <th className="col">Essentials</th>
                  <th className="col hot">Pro</th>
                  <th className="col">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {CMP.map((sec) => (
                  <React.Fragment key={sec.group}>
                    <tr>
                      <td className="feat" colSpan={4} style={{ fontWeight: 700, color: 'var(--brand)', background: 'var(--brand-soft)' }}>
                        {sec.group}
                      </td>
                    </tr>
                    {sec.rows.map((r) => (
                      <tr key={r.label}>
                        <td className="feat">{r.label}</td>
                        <td className="col">{r.e}</td>
                        <td className="col">{r.p}</td>
                        <td className="col">{r.x}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="cfo-section">
        <div className="cfo-wrap">
          <span className="cfo-eyebrow">Questions</span>
          <h2>Good to know.</h2>
          <div className="cfo-faq">
            {FAQS.map((f) => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="cfo-finalcta">
        <h2>See your own numbers in minutes.</h2>
        <p>QuickBooks is available today. Xero is private beta; Sage Intacct, Sage Accounting, and NetSuite are planned.</p>
        <button className="cfo-btn cfo-btn-primary" onClick={goSignup}>Connect your books — free →</button>
      </section>

      <CfoSiteFooter />
    </div>
  )
}

export default CfoPricingPage
